import { withScope } from '@sentry/react';

import { AttendedMeeting, GoogleCalendarEvent, isAttendedMeeting } from '@/api/custom';
import { captureMessage } from '@/sentry';

type MeetingNotesUsageAction = 'view' | 'edit' | 'save' | 'copy' | 'download';
type MeetingNotesUsageArtifact = 'meeting' | 'notes' | 'transcript' | 'recording';
type MeetingNotesUsageResult = 'attempted' | 'succeeded' | 'failed';

type MeetingNotesUsageEvent = {
  action: MeetingNotesUsageAction;
  artifact: MeetingNotesUsageArtifact;
  meeting: GoogleCalendarEvent | AttendedMeeting | null;
  result?: MeetingNotesUsageResult;
};

export const trackMeetingNotesUsage = ({
  action,
  artifact,
  meeting,
  result = 'attempted',
}: MeetingNotesUsageEvent): void => {
  if (!meeting || !isAttendedMeeting(meeting)) return;

  const context = {
    feature: 'meeting_notes',
    page: 'meeting_hub',
    action,
    artifact,
    result,
    job_id: meeting.job_id,
    status: meeting.status,
    meeting_memory_status: meeting.meeting_memory_status ?? 'not_applicable',
    has_summary_file: Boolean(meeting.summary_file_key),
    has_transcript_file: Boolean(meeting.transcript_file_key),
    has_recording: Boolean(meeting.recording_link),
  };

  try {
    withScope((scope) => {
      // Global browser context can contain advisor names, URLs and note text.
      // Usage reporting owns a small allowlist instead of inheriting that data.
      scope.addEventProcessor((event) => ({
        event_id: event.event_id,
        timestamp: event.timestamp,
        platform: event.platform,
        level: event.level,
        message: event.message,
        environment: event.environment,
        release: event.release,
        extra: context,
      }));
      captureMessage(`meeting_notes_usage.${action}.${artifact}.${result}`, 'info', context);
    });
  } catch {
    // Telemetry must not interrupt Meeting Hub actions.
  }
};
